import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { WarningIcon, SpinnerIcon } from '@phosphor-icons/react';
import {
  useCreateProductGroup,
  useUpdateProductGroup,
  ProductGroup,
} from 'src/api/rebates/useProductGroups';
import { ALL_OTHER_PRODUCTS, NON_REBATABLE_PRODUCTS } from '@erp/shared-utils';

/** Parse pipe-separated names into trimmed, non-empty values */
function parsePipeSeparatedNames(input: string): string[] {
  return input
    .split('|')
    .map((name) => name.trim())
    .filter((name) => name.length > 0);
}

interface Props {
  open: boolean;
  onClose: () => void;
  supplierId: string;
  productGroup?: ProductGroup; // If provided, we're editing; if not, we're creating
  onSuccess?: () => void;
}

const WARNING_NAMES = [
  'all',
  'other',
  'various',
  'nonrebatable',
  'non rebateable',
  'non-rebatable',
  'misc',
  'general',
  'default',
];

const ProductGroupModalShadcn: React.FC<Props> = ({
  open,
  onClose,
  supplierId,
  productGroup,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isHistorical: false,
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const createMutation = useCreateProductGroup();
  const updateMutation = useUpdateProductGroup(productGroup?.id || '');

  const isEditing = !!productGroup;
  const isLoading = createMutation.isLoading || updateMutation.isLoading;
  const isCatchAll = productGroup?.isCatchAll ?? false;

  // Parse names when creating (supports pipe-separated values)
  const parsedNames = useMemo(() => {
    if (isEditing) {
      return [formData.name.trim()];
    }
    return parsePipeSeparatedNames(formData.name);
  }, [formData.name, isEditing]);

  const isMultipleGroups = !isEditing && parsedNames.length > 1;

  // Only validate against reserved names if NOT a catch-all group
  const isInvalidName =
    !isCatchAll &&
    parsedNames.some(
      (name) =>
        name.toLowerCase() === ALL_OTHER_PRODUCTS.toLowerCase() ||
        name.toLowerCase() === NON_REBATABLE_PRODUCTS.toLowerCase(),
    );

  // Only show warning if NOT a catch-all group
  const isWarningName =
    !isCatchAll &&
    parsedNames.some((name) =>
      WARNING_NAMES.some((warningName) =>
        name.toLowerCase().includes(warningName.toLowerCase()),
      ),
    );

  // Reset form when modal opens/closes or productGroup changes
  useEffect(() => {
    if (open) {
      if (productGroup) {
        setFormData({
          name: productGroup.name,
          description: productGroup.description || '',
          // Catch-all groups cannot be historical
          isHistorical: productGroup.isCatchAll
            ? false
            : productGroup.isHistorical || false,
        });
      } else {
        setFormData({
          name: '',
          description: '',
          isHistorical: false,
        });
      }
      setErrors({});
    }
  }, [open, productGroup]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (parsedNames.length === 0) {
      newErrors.name = 'Name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (isEditing) {
        await updateMutation.mutateAsync({
          name: formData.name.trim(),
          description: formData.description.trim(),
          // Catch-all groups cannot be historical
          isHistorical: isCatchAll ? false : formData.isHistorical,
        });
      } else {
        await createMutation.mutateAsync({
          supplierId: supplierId,
          name: formData.name.trim(),
          description: formData.description.trim(),
          // Catch-all groups cannot be historical
          isHistorical: isCatchAll ? false : formData.isHistorical,
        });
      }

      // Success
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error saving product group:', error);
    }
  };

  const handleInputChange =
    (field: keyof typeof formData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: '',
        }));
      }
    };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Product Group' : 'Create Product Group'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the product group information below.'
              : 'Create a new product group for this supplier.'}
          </DialogDescription>
        </DialogHeader>

        {isCatchAll && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-blue-50 border border-blue-200 text-blue-900 text-sm">
            <Badge variant="secondary" className="shrink-0 mt-0.5">
              Default Group
            </Badge>
            <div className="flex-1">
              <p className="font-medium mb-1">Special Product Group</p>
              <p className="text-xs text-blue-800">
                This is the default catch-all product group. Items not assigned
                to specific product groups will be assigned here. This group
                cannot be deleted or marked as historical.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">
                {isEditing ? 'Name' : 'Name(s)'}{' '}
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={handleInputChange('name')}
                placeholder={
                  isEditing
                    ? 'Enter product group name'
                    : 'Enter names (use | to separate multiple)'
                }
                disabled={isLoading}
                className={errors.name ? 'border-red-500' : ''}
              />
              {!isEditing && (
                <p className="text-xs text-muted-foreground">
                  Tip: Use pipe (|) to create multiple groups at once, e.g.
                  "Apples | Oranges | Bananas"
                </p>
              )}
              {errors.name && (
                <span className="text-sm text-red-500">{errors.name}</span>
              )}
              {isMultipleGroups && (
                <div className="flex flex-wrap gap-1 mt-1">
                  <span className="text-xs text-muted-foreground mr-1">
                    Will create {parsedNames.length} groups:
                  </span>
                  {parsedNames.map((name, idx) => (
                    <Badge
                      key={idx}
                      variant="secondary"
                      className="text-xs py-0"
                    >
                      {name}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={handleInputChange('description')}
                placeholder="Enter product group description (optional)"
                disabled={isLoading}
                rows={3}
              />
            </div>

            {!isCatchAll && (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="isHistorical">Historical</Label>
                    <p className="text-xs text-muted-foreground">
                      Mark this product group as historical (excluded from new
                      processing)
                    </p>
                  </div>
                  <Switch
                    id="isHistorical"
                    checked={formData.isHistorical}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({
                        ...prev,
                        isHistorical: checked,
                      }))
                    }
                    disabled={isLoading}
                  />
                </div>

                {formData.isHistorical && (
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="secondary"
                      className="bg-gray-100 text-gray-800"
                    >
                      Historical
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      This product group will be excluded from new rebate
                      processing
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          {isInvalidName && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-red-800 text-sm mb-4">
              <WarningIcon className="h-4 w-4 text-yellow-600 flex-shrink-0" />
              <span>
                <strong>{formData.name}</strong> is not a valid product group
                name.
              </span>
            </div>
          )}
          {isWarningName && !isInvalidName && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm mb-4">
              <WarningIcon className="h-4 w-4 text-yellow-600 flex-shrink-0" />
              <span>
                <strong>{formData.name}</strong> might be captured by the
                default product group or non rebatable product group.
              </span>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || isInvalidName}>
              {isLoading && (
                <SpinnerIcon className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isEditing
                ? 'Update'
                : isMultipleGroups
                  ? `Create ${parsedNames.length} Groups`
                  : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProductGroupModalShadcn;
